<template>
  <div class="contract-settings">
    <div class="settings-content-wrapper">
      <div v-if="activeTab === 'template'">
        <div class="settings-toolbar">
          <div class="toolbar-title">
            <span class="toolbar-icon">
              <AppIcon name="bi-file-earmark-richtext" class="bi bi-file-earmark-richtext" />
            </span>
            <h5 class="mb-0">{{ $t('contract_templates') }}</h5>
          </div>

          <button @click="createTemplate" class="btn btn-primary btn-sm toolbar-action">
            <AppIcon name="bi-plus-lg" class="bi bi-plus-lg" />
            <span>{{ $t('add_template') }}</span>
          </button>
        </div>

        <div v-if="loading" class="templates-loading">
          <div class="spinner-border spinner-border-sm text-primary" role="status">
            <span class="visually-hidden">{{ $t('loading') }}...</span>
          </div>
          <span>{{ $t('loading_templates') }}...</span>
        </div>

        <div v-else-if="templates.length > 0" class="template-grid">
          <article v-for="template in templates" :key="template.id" class="template-card">
            <div class="template-preview" aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
            </div>

            <div class="template-info">
              <h6>{{ template.title }}</h6>
              <span>{{ $t('last_updated') }} {{ formatDate(template.updated_at) }}</span>
            </div>

            <div class="template-actions">
              <button @click="editTemplate(template)" class="icon-button edit" type="button" :title="$t('edit')">
                <AppIcon name="bi-pencil" class="bi bi-pencil" />
              </button>
              <button @click="deleteRow(template)" class="icon-button delete" type="button" :title="$t('delete')">
                <AppIcon name="bi-trash" class="bi bi-trash" />
              </button>
            </div>
          </article>
        </div>

        <div v-else class="empty-state-wrapper">
          <span class="empty-icon-bg">
            <AppIcon name="bi-file-earmark-plus" class="bi bi-file-earmark-plus" />
          </span>
          <h5>{{ $t('no_templates_found') }}</h5>
          <button class="btn btn-primary btn-sm toolbar-action" @click="createTemplate">
            <AppIcon name="bi-plus-lg" class="bi bi-plus-lg" />
            <span>{{ $t('create_your_first_template') }}</span>
          </button>
        </div>
      </div>
    </div>

    <AddEditContractTemplateModal ref="addEditModal" :contractData="selectedContract" @saved="getTemplates" />

  </div>
</template>

<script>
import helper from '../../helper';
import AddEditContractTemplateModal from '../../components/contract/AddEditContractTemplateModal.vue';

export default {
  name: 'ContractSettings',
  components: {
    AddEditContractTemplateModal
  },
  props: {
    activeTab: {
      type: String,
      default: 'template'
    }
  },
  data() {
    return {
      templates: [],
      loading: false,
      selectedContract: null
    };
  },
  mounted() {
    if (this.activeTab === 'template') {
        this.getTemplates();
    }
  },
  watch: {
    activeTab(newTab) {
        if (newTab === 'template') {
            this.getTemplates();
        }
    }
  },
  methods: {
    formatDate(date) {
        return helper.formatDate(date);
    },
    async getTemplates() {
        try {
            this.loading = true;
            const url = `${process.env.VUE_APP_API}/contracts?is_template=true&per_page=100`;
            const response = await helper.customGet(url);
            this.templates = response.data.data;
            this.loading = false;
        } catch (error) {
            this.loading = false;
        }
    },
    createTemplate() {
        this.selectedContract = null;
        this.$nextTick(() => {
            this.$refs.addEditModal.show('general', true);
        });
    },
    editTemplate(template) {
        this.selectedContract = template;
        this.$nextTick(() => {
            this.$refs.addEditModal.show('general', true);
        });
    },
    async deleteRow(row){
        const result = await this.$swal.fire({
            title: `${this.$t('are_you_sure')}?`,
            text : this.$t('you_cant_revert'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: this.$t('yes_delete_it'),
            cancelButtonText: this.$t('cancel'),
            confirmButtonColor: '#d33',
            cancelButtonColor: '#004aad',
            reverseButtons: true
        })

        if (result.isConfirmed) {
            try {
                await helper.customDelete(`${process.env.VUE_APP_API}/contracts/${row.id}`)
                this.templates = helper.withoutDeletedRecords(this.templates, row.id)
            } catch (error) {
                this.$toast.open({ 
                    message: this.$t('error_deleting_template'), 
                    type: 'error' 
                });
            }
        }
    }
  }
};
</script>

<style scoped>
.contract-settings {
  min-height: 100%;
  padding: 0;
  background: #f8f9fc;
}

.contract-settings .btn-primary {
  background-color: #004aad;
  border-color: #004aad;
}

.contract-settings .btn-primary:hover,
.contract-settings .btn-primary:focus {
  background-color: #1f1f1f;
  border-color: #1f1f1f;
}

.contract-settings .text-primary {
  color: #303030 !important;
}

.settings-content-wrapper {
  height: 100%;
  max-height: calc(95vh - 140px);
  overflow-y: auto;
  padding: 24px;
}

.settings-toolbar {
  align-items: center;
  background: #fff;
  border: 1px solid #e5e8f0;
  border-radius: 12px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 14px;
}

.toolbar-title {
  align-items: center;
  display: flex;
  gap: 10px;
}

.toolbar-title h5 {
  color: #0f172a;
  font-size: 1rem;
  font-weight: 800;
}

.toolbar-icon {
  align-items: center;
  background: #f1f1f1;
  border-radius: 10px;
  color: #303030;
  display: inline-flex;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.toolbar-action {
  align-items: center;
  display: inline-flex;
  font-weight: 700;
  gap: 8px;
  min-height: 34px;
}

.templates-loading {
  align-items: center;
  background: #fff;
  border: 1px solid #e5e8f0;
  border-radius: 12px;
  color: #64748b;
  display: flex;
  font-size: 0.86rem;
  gap: 10px;
  justify-content: center;
  min-height: 220px;
}

.template-grid {
  display: grid;
  gap: 12px;
}

.template-card {
  align-items: center;
  background: #fff;
  border: 1px solid #e5e8f0;
  border-radius: 12px;
  display: grid;
  gap: 14px;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  min-height: 74px;
  padding: 12px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.template-card:hover {
  border-color: #bdbdbd;
  box-shadow: 0 12px 28px rgba(31, 38, 55, 0.08);
  transform: translateY(-1px);
}

.template-preview {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: 48px;
  justify-content: center;
  padding: 8px;
}

.template-preview span {
  background: #cbd5e1;
  border-radius: 999px;
  display: block;
  height: 4px;
}

.template-preview span:first-child {
  background: #303030;
  width: 72%;
}

.template-preview span:nth-child(2) {
  width: 100%;
}

.template-preview span:last-child {
  width: 58%;
}

.template-info {
  min-width: 0;
}

.template-info h6 {
  color: #0f172a;
  font-size: 0.95rem;
  font-weight: 800;
  margin: 0 0 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.template-info span {
  color: #64748b;
  display: block;
  font-size: 0.76rem;
}

.template-actions {
  display: flex;
  gap: 8px;
}

.icon-button {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #334155;
  display: inline-flex;
  height: 34px;
  justify-content: center;
  padding: 0;
  transition: background 0.16s ease, border-color 0.16s ease, color 0.16s ease;
  width: 34px;
}

.icon-button.edit:hover {
  background: #f1f1f1;
  border-color: #bdbdbd;
  color: #334155;
}

.icon-button.delete:hover {
  background: #fef2f2;
  border-color: #fecaca;
  color: #dc2626;
}

.empty-state-wrapper {
  align-items: center;
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  color: #0f172a;
  display: flex;
  flex-direction: column;
  gap: 14px;
  justify-content: center;
  min-height: 320px;
  padding: 32px 18px;
}

.empty-state-wrapper h5 {
  font-size: 1rem;
  font-weight: 800;
  margin: 0;
}

.empty-icon-bg {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  color: #64748b;
  display: inline-flex;
  font-size: 1.6rem;
  height: 54px;
  justify-content: center;
  width: 54px;
}

.settings-content-wrapper::-webkit-scrollbar {
  width: 6px;
}

.settings-content-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.settings-content-wrapper::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 10px;
}

.settings-content-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

@media (max-width: 767.98px) {
  .settings-content-wrapper {
    padding: 14px;
  }

  .settings-toolbar,
  .template-card {
    align-items: stretch;
    grid-template-columns: 1fr;
  }

  .settings-toolbar {
    flex-direction: column;
  }

  .toolbar-action,
  .template-actions {
    width: 100%;
  }

  .template-actions .icon-button {
    flex: 1 1 auto;
  }
}
</style>
